import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/header/header";
import Footer from "../components/footer/footer";
import LogoCigna from "../../assets/img/logo-cigna.svg";
import LogoHumana from "../../assets/img/logo-humana.png";
import LogoMolina from "../../assets/img/logo-molina.svg";
import LogoUnited from "../../assets/img/logo-united.svg";
import LogoAetna from "../../assets/img/logo-aetna.svg";
import locations from "./locationsConfig";
import "./conditionLocationPage.scss";

const BOOKING_URL =
    "https://consumer.scheduling.athena.io/?locationId=20729-1&practitionerId=20729-1";

const SITE_ORIGIN = "https://www.virtualpsych.com";

// --- Helpers -------------------------------------------------------------
const upsertMeta = (selector, attrs) => {
    let tag = document.head.querySelector(selector);
    let created = false;
    if (!tag) {
        tag = document.createElement(attrs.tagName || "meta");
        Object.entries(attrs.create || {}).forEach(([k, v]) =>
            tag.setAttribute(k, v),
        );
        document.head.appendChild(tag);
        created = true;
    }
    const prev = {};
    Object.entries(attrs.set || {}).forEach(([k, v]) => {
        prev[k] = tag.getAttribute(k);
        tag.setAttribute(k, v);
    });
    return { tag, prev, created };
};

const upsertJsonLd = (id, data) => {
    let tag = document.head.querySelector(`script[data-seo-id="${id}"]`);
    const created = !tag;
    if (!tag) {
        tag = document.createElement("script");
        tag.type = "application/ld+json";
        tag.setAttribute("data-seo-id", id);
        document.head.appendChild(tag);
    }
    const prev = tag.textContent;
    tag.textContent = JSON.stringify(data);
    return { tag, prev, created };
};

const buildFaqs = ({ condition, city, state }) => {
    const lc = condition.toLowerCase();
    return [
        {
            q: `How do I see a psychiatrist for ${lc} in ${city}, ${state}?`,
            a: `Book an online appointment with VirtualPsych and meet with a ${state}-licensed psychiatrist over secure video. Most ${city} patients are seen within a week and can be evaluated, diagnosed, and prescribed treatment without ever leaving home.`,
        },
        {
            q: `Can a VirtualPsych psychiatrist prescribe medication for ${lc} in ${state}?`,
            a: `Yes. Our psychiatrists are licensed to prescribe in ${state}, and prescriptions are sent electronically to your preferred pharmacy when clinically appropriate.`,
        },
        {
            q: `Does VirtualPsych accept insurance in ${state}?`,
            a: `VirtualPsych accepts most major insurance plans including Cigna, Humana, Molina, Aetna, and UnitedHealthcare. Self-pay options are also available for ${city} residents who prefer to skip insurance.`,
        },
        {
            q: `Are VirtualPsych ${lc} appointments confidential?`,
            a: `All visits are conducted over a secure, HIPAA-compliant video platform, and your health information is treated with the same strict confidentiality as an in-person ${city} psychiatry visit.`,
        },
        {
            q: `How quickly can I get a ${lc} appointment in ${city}?`,
            a: `Most new patients in ${city}, ${state} can schedule an initial ${lc} consultation within a few days. Same-week availability is common.`,
        },
    ];
};

// --- Component -----------------------------------------------------------
const ConditionLocationPage = ({ entry }) => {
    const { condition, city, state, stateAbbr, slug } = entry;

    const title = `${condition} Psychiatrists in ${city}, ${stateAbbr}`;
    const pageDescription = `Speak with a licensed psychiatrist from ${city}, ${state} about ${condition.toLowerCase()}. Online appointments, medication management, and follow-up care from VirtualPsych.`;
    const canonicalUrl = `${SITE_ORIGIN}/${slug}`;
    const faqs = buildFaqs({ condition, city, state });

    // Related links: other cities w/ same condition, and other conditions in same city
    const relatedCities = locations
        .filter(
            (l) =>
                l.condition === condition &&
                !(l.city === city && l.stateAbbr === stateAbbr),
        )
        .slice(0, 6);
    const relatedConditions = locations
        .filter(
            (l) =>
                l.city === city &&
                l.stateAbbr === stateAbbr &&
                l.condition !== condition,
        )
        .slice(0, 6);

    useEffect(() => {
        const prevTitle = document.title;
        document.title = `${title} | VirtualPsych`;

        const cleanups = [];

        // Description
        cleanups.push(
            upsertMeta('meta[name="description"]', {
                create: { name: "description" },
                set: { content: pageDescription },
            }),
        );

        // Canonical
        cleanups.push(
            upsertMeta('link[rel="canonical"]', {
                tagName: "link",
                create: { rel: "canonical" },
                set: { href: canonicalUrl },
            }),
        );

        // Open Graph
        const og = [
            ["og:type", "website"],
            ["og:title", `${title} | VirtualPsych`],
            ["og:description", pageDescription],
            ["og:url", canonicalUrl],
            ["og:site_name", "VirtualPsych"],
            ["og:image", `${SITE_ORIGIN}/logo512.png`],
        ];
        og.forEach(([property, content]) => {
            cleanups.push(
                upsertMeta(`meta[property="${property}"]`, {
                    create: { property },
                    set: { content },
                }),
            );
        });

        // Twitter
        const tw = [
            ["twitter:card", "summary_large_image"],
            ["twitter:title", `${title} | VirtualPsych`],
            ["twitter:description", pageDescription],
            ["twitter:image", `${SITE_ORIGIN}/logo512.png`],
        ];
        tw.forEach(([name, content]) => {
            cleanups.push(
                upsertMeta(`meta[name="${name}"]`, {
                    create: { name },
                    set: { content },
                }),
            );
        });

        // Robots
        cleanups.push(
            upsertMeta('meta[name="robots"]', {
                create: { name: "robots" },
                set: { content: "index, follow, max-image-preview:large" },
            }),
        );

        // --- JSON-LD ---
        const medicalBusiness = {
            "@context": "https://schema.org",
            "@type": "MedicalBusiness",
            name: `VirtualPsych — ${condition} Psychiatrists serving ${city}, ${stateAbbr}`,
            url: canonicalUrl,
            telephone: "+1-877-839-4824",
            medicalSpecialty: "Psychiatry",
            description: pageDescription,
            areaServed: {
                "@type": "City",
                name: city,
                containedInPlace: {
                    "@type": "State",
                    name: state,
                },
            },
            availableService: {
                "@type": "MedicalTherapy",
                name: `Online ${condition} Treatment`,
                serviceType: `Telepsychiatry for ${condition}`,
            },
            potentialAction: {
                "@type": "ReserveAction",
                target: BOOKING_URL,
                name: "Book an Appointment",
            },
        };

        const breadcrumb = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
                {
                    "@type": "ListItem",
                    position: 2,
                    name: `${condition} Psychiatrists`,
                    item: canonicalUrl,
                },
                {
                    "@type": "ListItem",
                    position: 3,
                    name: `${city}, ${stateAbbr}`,
                    item: canonicalUrl,
                },
            ],
        };

        const faqPage = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map(({ q, a }) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
            })),
        };

        const ldCleanups = [
            upsertJsonLd("clp-medbusiness", medicalBusiness),
            upsertJsonLd("clp-breadcrumb", breadcrumb),
            upsertJsonLd("clp-faqpage", faqPage),
        ];

        return () => {
            document.title = prevTitle;
            cleanups.forEach(({ tag, prev, created }) => {
                if (created) {
                    tag.remove();
                } else {
                    Object.entries(prev).forEach(([k, v]) => {
                        if (v === null) tag.removeAttribute(k);
                        else tag.setAttribute(k, v);
                    });
                }
            });
            ldCleanups.forEach(({ tag, prev, created }) => {
                if (created) tag.remove();
                else tag.textContent = prev;
            });
        };
    }, [title, pageDescription, canonicalUrl, condition, city, state, stateAbbr, faqs]);

    return (
        <div className="condition-location-page">
            <Header />

            <main>
                <section className="clp-hero" aria-labelledby="clp-hero-title">
                    <div className="container">
                        <h1 id="clp-hero-title">{title}</h1>
                        <p className="clp-hero-sub">
                            Connect with licensed psychiatrists serving {city}, {state} for
                            {" "}{condition.toLowerCase()} evaluation, treatment, and ongoing care —
                            all from the comfort of home.
                        </p>
                        <a
                            href={BOOKING_URL}
                            className="button pri-button clp-cta"
                            style={{ color: "#fff" }}
                            aria-label={`Book an online ${condition.toLowerCase()} appointment in ${city}, ${stateAbbr}`}
                        >
                            Book an Appointment
                        </a>
                    </div>
                </section>

                <section
                    className="clp-intro container"
                    aria-labelledby="clp-intro-title"
                >
                    <h2 id="clp-intro-title">
                        Online {condition} Care in {city}
                    </h2>
                    <p>
                        VirtualPsych makes it easy for {city} residents to see a board-certified
                        psychiatrist online. Whether you've been managing {condition.toLowerCase()} for
                        years or are reaching out for the first time, our {state}-licensed clinicians
                        are here to help with personalized treatment plans, medication management,
                        and compassionate follow-up care.
                    </p>
                    <ul className="clp-list">
                        <li>Same-week appointments available</li>
                        <li>Confidential video visits from anywhere in {state}</li>
                        <li>Most major insurance plans accepted</li>
                        <li>Prescriptions sent to your pharmacy</li>
                    </ul>
                </section>

                <section
                    className="clp-steps"
                    aria-labelledby="clp-steps-title"
                >
                    <div className="container">
                        <h2 id="clp-steps-title">How it works in {city}</h2>
                        <ol className="clp-steps-list">
                            <li>
                                <span className="clp-step-num">1</span>
                                <span className="clp-step-text">
                                    Tell us about your {condition.toLowerCase()} symptoms and goals.
                                </span>
                            </li>
                            <li>
                                <span className="clp-step-num">2</span>
                                <span className="clp-step-text">
                                    Pick a {state}-licensed psychiatrist who fits your needs.
                                </span>
                            </li>
                            <li>
                                <span className="clp-step-num">3</span>
                                <span className="clp-step-text">
                                    Meet over secure video and start your personalized care plan.
                                </span>
                            </li>
                        </ol>
                    </div>
                </section>

                <div
                    className="home-insurance-strip"
                    aria-label="Insurance plans accepted"
                >
                    <div className="container">
                        <p className="home-insurance-title">
                            We accept most major insurances
                        </p>
                        <div className="home-insurance-logos">
                            <img src={LogoCigna} alt="Cigna insurance accepted" />
                            <img src={LogoHumana} alt="Humana insurance accepted" className="logo-humana" />
                            <img src={LogoMolina} alt="Molina Healthcare insurance accepted" className="logo-molina" />
                            <img src={LogoAetna} alt="Aetna insurance accepted" />
                            <img src={LogoUnited} alt="UnitedHealthcare insurance accepted" className="logo-united" />
                        </div>
                    </div>
                </div>

                <section
                    className="clp-faq"
                    aria-labelledby="clp-faq-title"
                >
                    <div className="container">
                        <h2 id="clp-faq-title">
                            Frequently asked questions about {condition.toLowerCase()} care in {city}
                        </h2>
                        <dl className="clp-faq-list">
                            {faqs.map(({ q, a }) => (
                                <div className="clp-faq-item" key={q}>
                                    <dt>{q}</dt>
                                    <dd>{a}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </section>

                <div className="clp-divider-band" aria-hidden="true" />

                {(relatedCities.length > 0 || relatedConditions.length > 0) && (
                    <section
                        className="clp-related"
                        aria-labelledby="clp-related-title"
                    >
                        <div className="container">
                            <h2 id="clp-related-title">Explore related care</h2>
                            <div className="clp-related-grid">
                                {relatedCities.length > 0 && (
                                    <div>
                                        <h3>
                                            {condition} psychiatrists in other cities
                                        </h3>
                                        <ul>
                                            {relatedCities.map((l) => (
                                                <li key={l.slug}>
                                                    <Link to={`/${l.slug}`}>
                                                        {l.condition} psychiatrists in {l.city}, {l.stateAbbr}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {relatedConditions.length > 0 && (
                                    <div>
                                        <h3>
                                            Other care available in {city}, {stateAbbr}
                                        </h3>
                                        <ul>
                                            {relatedConditions.map((l) => (
                                                <li key={l.slug}>
                                                    <Link to={`/${l.slug}`}>
                                                        {l.condition} psychiatrists in {l.city}, {l.stateAbbr}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                )}

                <section className="clp-cta-band" aria-labelledby="clp-cta-title">
                    <div className="container">
                        <h2 id="clp-cta-title">
                            Ready to talk to a psychiatrist from {city}?
                        </h2>
                        <p>
                            Schedule an online {condition.toLowerCase()} consultation with a VirtualPsych
                            provider today.
                        </p>
                        <a
                            href={BOOKING_URL}
                            className="button pri-button"
                            style={{ color: "#fff" }}
                            aria-label={`Get started with an online ${condition.toLowerCase()} appointment in ${city}, ${stateAbbr}`}
                        >
                            Get Started
                        </a>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default ConditionLocationPage;
